/**
 * Canonical Deal Flow view model — ONE adapter from the persisted opportunity
 * document to the normalized shape Cards and Table both consume.
 *
 * The browser is a view layer only. Every value here is read from the persisted
 * opportunity document (never invented, never estimated). Metrics the pipeline
 * does not persist are left null so the UI renders "—" truthfully.
 */

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s || null;
}

function hostOf(url) {
  try {
    return new URL(String(url)).hostname.replace(/^www\./i, "") || null;
  } catch {
    return null;
  }
}

/** Canonical match-status → availability-independent selling label. */
export const MATCH_STATUS_LABEL = {
  verified: "Verified match",
  exact: "Exact match",
  review: "Needs review",
  probable: "Probable match",
  rejected: "Rejected",
};

/** Quick Winner opportunity_status → human label. */
export const OPPORTUNITY_STATUS_LABEL = {
  WINNING: "Winning",
  ESTIMATED: "Estimated",
  NEEDS_REVIEW: "Needs review",
  STALE: "Stale",
  INVALID: "Invalid",
};

/** Short explainer under each status chip — what the badge means. */
export const OPPORTUNITY_STATUS_HINT = {
  WINNING: "Verified active offer, source stock, and complete fulfillment economics",
  ESTIMATED: "Promising match with outstanding stock confirmation or incomplete fulfillment fees",
  NEEDS_REVIEW: "Missing identity, price, cost, availability, or other essential data",
  STALE: "Price or verification evidence is older than the freshness window",
  INVALID: "Rejected match or confirmed unavailable listing",
};

/** Humanize a persisted review_reason code for Needs Review cards. */
export function formatReviewReason(code) {
  const s = String(code || "").trim();
  if (!s) return null;
  const map = {
    economics_missing: "Economics not calculated",
    economics_incomplete: "Incomplete cost inputs",
    missing_source_price: "Missing source price",
    missing_walmart_price: "Missing Walmart price",
    missing_acquisition_cost: "Missing acquisition cost",
    walmart_availability_unknown: "Walmart availability unknown",
    source_availability_unknown: "Source availability unknown",
    walmart_unavailable: "Walmart listing unavailable",
    source_unavailable: "Source product unavailable",
    missing_verification_timestamp: "Missing verification timestamp",
    match_review: "Match needs review",
    match_probable: "Probable match only",
    match_rejected: "Match rejected",
    wfs_fees_not_included: "WFS fees not included",
    storage_fees_not_included: "Storage fees not included",
    seller_fulfilled_outbound_not_included: "Seller-fulfilled outbound not included",
    incomplete_fulfillment_economics: "Incomplete fulfillment economics",
    needs_review: "Needs review",
    marketplace_catalog_no_stock_field: "Catalog listing has no stock field",
    estimated_not_confirmed_offer: "Estimated — Walmart offer not confirmed in stock",
  };
  if (map[s]) return map[s];
  if (s.startsWith("stale_evidence:")) return `Stale evidence (${s.slice("stale_evidence:".length)})`;
  if (s.startsWith("below_min_profit:")) return `Below min profit (${s.slice("below_min_profit:".length)})`;
  if (s.startsWith("below_min_roi:")) return `Below min ROI (${s.slice("below_min_roi:".length)})`;
  if (s.startsWith("missing:")) return `Missing ${s.slice("missing:".length).replace(/_/g, " ")}`;
  return s.replace(/_/g, " ");
}

/** Walmart availability → canonical selling badge. */
export function walmartSellingStatus(availability) {
  const a = (availability || "").toLowerCase();
  if (!a) return "UNKNOWN";
  if (a.includes("out of stock") || a.replace(/\s|_/g, "").includes("outofstock")) {
    return "OUT_OF_STOCK";
  }
  if (a.includes("unavailable") || a.includes("inactive")) return "UNAVAILABLE";
  if (a.includes("in stock") || a.includes("available")) return "ACTIVE";
  return "UNKNOWN";
}

export const SELLING_STATUS_LABEL = {
  ACTIVE: "Selling",
  OUT_OF_STOCK: "Out of stock",
  UNAVAILABLE: "Unavailable",
  UNKNOWN: "Availability unknown",
};

/**
 * @param {object} opp persisted opportunity document (source/walmart/match/price_spread)
 * @returns {object} canonical Deal Flow lead
 */
export function mapDealFlowLead(opp) {
  const src = opp?.source || {};
  const wm = opp?.walmart || {};
  const match = opp?.match || {};
  const spread = opp?.price_spread;
  const economics = opp?.economics || {};

  const walmartItemId = str(wm.item_id) || str(wm.itemId);
  const walmartPrice = num(wm.price);
  const sourcePrice = num(src.price);
  const absoluteSpread = num(spread?.absolute_spread);
  const grossSpread = num(opp?.gross_spread?.absolute_spread) ?? num(opp?.gross_spread);
  // Prefer verified price_spread; fall back to gross spread when prices exist.
  const displaySpread =
    absoluteSpread ??
    grossSpread ??
    (walmartPrice != null && sourcePrice != null
      ? Number((walmartPrice - sourcePrice).toFixed(2))
      : null);
  const status = str(match.status) || "unknown";
  const canAnalyze = Boolean(walmartItemId) && status !== "rejected";
  const opportunityStatus = str(opp?.opportunity_status);
  const reviewReasonsRaw = Array.isArray(opp?.review_reasons) ? opp.review_reasons : [];
  const sourceReviewReasons = Array.isArray(opp?.source_review_reasons)
    ? opp.source_review_reasons
    : [];
  const reviewReasons = [...reviewReasonsRaw, ...sourceReviewReasons]
    .map((r) => String(r || "").trim())
    .filter(Boolean)
    .filter((r, i, arr) => arr.indexOf(r) === i);
  // Persisted economics only — never invent profit/ROI in the browser.
  // Stage F: show preliminary figures when incomplete (card labels "Fees needed").
  const profit = num(economics.estimated_profit);
  const roi = num(economics.estimated_roi);
  const margin = num(economics.margin);
  const fees = num(economics.marketplace_fees);
  const referralFee = num(economics.referral_fee) ?? (economics.excludes_wfs === true ? fees : null);
  const wfsFee = num(economics.wfs_fee);
  const fulfillmentFee = num(economics.fulfillment_fee);
  const economicsComplete = economics.complete === true;

  return {
    id: str(opp?.id) || str(opp?.opportunity_key),
    marketplace: "walmart",

    // ── source (acquisition) side ────────────────────────────────────────────
    // source_retailer is the canonical source_id (e.g. michaels_com) — required
    // for multi-retailer All Products filters after endurance runs.
    sourceId: str(src.source_retailer) || str(src.source_id) || null,
    sourceMerchant: str(src.source_retailer_name) || str(src.source_retailer) || "Source",
    sourceDomain: hostOf(src.source_url) || str(src.source_retailer),
    sourceUrl: str(src.source_url) || str(src.canonical_url),
    sourceImage: str(src.image) || str(src.image_url),
    sourceTitle: str(src.title),
    sourcePrice,
    // Stage S: a store-specific source price must be visibly labelled, never
    // presented as a nationwide online acquisition cost.
    sourcePriceScope: src.price_scope ?? src.source_price_scope ?? null,
    sourcePriceLocationId:
      src.price_location_id ?? src.source_price_location_id ?? null,
    sourceCurrency: str(src.currency) || "USD",
    sourceAvailability: str(src.availability),
    sourceBrand: str(src.brand),
    sourceCheckedAt: str(src.collected_at) || str(src.observed_at),

    // ── Walmart (destination) side ───────────────────────────────────────────
    walmartItemId,
    walmartUrl: str(wm.url) || (walmartItemId ? `https://www.walmart.com/ip/${walmartItemId}` : null),
    walmartImage:
      str(wm.image) ||
      str(wm.imageUrl) ||
      str(wm.image_url) ||
      (Array.isArray(wm.images) && wm.images.length
        ? str(typeof wm.images[0] === "object" ? wm.images[0]?.url || wm.images[0]?.imageUrl : wm.images[0])
        : null),
    walmartImages: Array.isArray(wm.images)
      ? wm.images
          .map((img) => str(typeof img === "object" ? img?.url || img?.imageUrl || img?.src : img))
          .filter(Boolean)
      : [],
    walmartTitle: str(wm.title),
    walmartPrice,
    walmartCurrency: str(wm.currency) || "USD",
    walmartAvailability: str(wm.availability),
    walmartAvailabilityEvidence: str(wm.availability_evidence),
    walmartListingEvidence: str(wm.listing_evidence),
    walmartSeller: str(wm.seller),
    walmartSellerCount: num(wm.seller_count),
    walmartBrand: str(wm.brand),
    walmartManufacturer: str(wm.manufacturer),
    walmartDescription: str(wm.description),
    walmartFeatures: Array.isArray(wm.features) ? wm.features.map(str).filter(Boolean) : [],
    walmartSpecifications: wm.specifications,
    walmartModel: str(wm.model),
    walmartMpn: str(wm.mpn),
    walmartSize: str(wm.size),
    walmartColor: str(wm.color),
    walmartCondition: str(wm.condition),
    walmartPack: str(wm.pack),
    walmartPackCount: num(wm.pack_count),
    walmartListPrice: num(wm.list_price),
    walmartFulfillment: wm.fulfillment,
    walmartDeliveryText: str(wm.delivery_text),
    walmartPickupText: str(wm.pickup_text),
    walmartShippingText: str(wm.shipping_text),
    walmartSoldAndShippedBy: str(wm.sold_and_shipped_by),
    walmartAdditionalOfferCount: num(wm.additional_offer_count),
    walmartAdditionalOffersStartingPrice: num(wm.additional_offers_starting_price),

    // ── ratings / reviews (real persisted values) ────────────────────────────
    rating: num(wm.rating),
    reviews: num(wm.reviews),
    // Back-compat alias: "sellers" == real seller_count (null when unknown).
    sellers: num(wm.seller_count),

    // ── source (acquisition) side enrichment ─────────────────────────────────
    sourceImage: str(src.image) || str(src.image_url),
    sourceImages: Array.isArray(src.images) ? src.images.map(str).filter(Boolean) : [],
    sourceDescription: str(src.description),
    sourceFeatures: Array.isArray(src.features) ? src.features.map(str).filter(Boolean) : [],
    sourceSpecifications: src.specifications,
    sourceModel: str(src.model),
    sourceMpn: str(src.mpn),
    sourceSize: str(src.size),
    sourceColor: str(src.color),
    sourcePackCount: num(src.pack_count),
    sourceCondition: str(src.condition),
    sourceShipping: src.shipping,
    sourceListPrice: num(src.list_price),

    // ── identity ─────────────────────────────────────────────────────────────
    upc: str(src.upc) || str(wm.upc),
    gtin: str(src.gtin) || str(wm.gtin),
    category: str(src.category),

    // ── economics (only what is persisted) ───────────────────────────────────
    spread: displaySpread,
    spreadPercent: num(spread?.spread_percent) ?? num(opp?.gross_spread?.spread_percent),
    // Show persisted estimates even when fees are incomplete — never invent.
    profit,
    roi,
    margin,
    fees: economicsComplete ? fees : null,
    referralFee: economicsComplete ? referralFee : null,
    wfsFee: economicsComplete ? wfsFee : null,
    fulfillmentFee: economicsComplete ? fulfillmentFee : null,
    economicsComplete,
    roiDenominator: str(opp?.opportunity_status_meta?.roi_denominator) || "effective_acquisition_cost",
    excludesWfs: economics.excludes_wfs === true,
    excludesStorage: economics.excludes_storage === true,

    // ── match ────────────────────────────────────────────────────────────────
    // ── Pack Differs / unit-normalized pricing (persisted only) ──────────────
    packDiffers: Boolean(
      match.label_code === "pack_differs"
      || opp?.pack_differs === true
      || (num(opp?.source_pack) && num(opp?.walmart_pack)
        && num(opp.source_pack) !== num(opp.walmart_pack)),
    ),
    sourceUnitPrice: num(opp?.source_unit_price) ?? num(opp?.gross_spread?.source_unit_price),
    walmartUnitPrice: num(opp?.walmart_unit_price) ?? num(opp?.gross_spread?.walmart_unit_price),
    unitSpread: num(opp?.unit_spread) ?? num(opp?.gross_spread?.absolute_spread),
    priceBasis: str(opp?.price_basis) || str(opp?.gross_spread?.price_basis) || "item",
    sourcePackLabel: num(opp?.source_pack),
    walmartPackLabel: num(opp?.walmart_pack),

    matchStatus: status,
    matchStatusLabel: MATCH_STATUS_LABEL[status] || null,
    matchConfidence: num(match.confidence),
    matchLabel: str(match.label) || str(opp?.match_label) || null,
    matchLabelCode: str(match.label_code) || str(opp?.match_label_code) || null,
    // Stage Y: identity contradicted upstream. The comparison is INVALID — raw
    // prices stay visible for diagnostics, but no eligible spread/profit.
    identityRejected: Boolean(opp?.identity_rejected || opp?.qualification === "IDENTITY_REJECTED"),
    // WHY this row is (or is not) a qualifying opportunity, straight from the
    // persisted backend verdict. Without these the card renders a plausible
    // "est. profit" for a product the customer's rules REJECTED, which is the
    // defect: a $3.45 estimate under a $10 minimum looked like a winner.
    qualifies: typeof opp?.qualifies === "boolean" ? opp.qualifies : null,
    qualification: str(opp?.qualification) || null,
    disqualificationReasons: Array.isArray(opp?.disqualification_reasons)
      ? opp.disqualification_reasons
      : [],
    evidenceGaps: Array.isArray(opp?.evidence_gaps) ? opp.evidence_gaps : [],
    criteriaConfigured: typeof opp?.criteria_configured === "boolean"
      ? opp.criteria_configured
      : null,
    matchTier: str(match.identity_tier),
    matchReason: str(match.decision_reason),
    matchEvidence: Array.isArray(match.evidence) ? match.evidence : [],
    matchRejectionReasons: Array.isArray(match.rejection_reasons) ? match.rejection_reasons : [],
    matchMatchedEvidence: Array.isArray(match.matched_evidence) ? match.matched_evidence : [],
    matchMissingEvidence: Array.isArray(match.missing_evidence) ? match.missing_evidence : [],

    // ── Quick Winner opportunity status ──────────────────────────────────────
    opportunityStatus,
    opportunityStatusLabel: opportunityStatus
      ? (OPPORTUNITY_STATUS_LABEL[opportunityStatus] || opportunityStatus)
      : null,
    opportunityStatusHint: opportunityStatus
      ? (OPPORTUNITY_STATUS_HINT[opportunityStatus] || null)
      : null,
    reviewReasons,
    reviewReasonLabels: reviewReasons.map(formatReviewReason).filter(Boolean),
    missingRequirements: (Array.isArray(opp?.estimated_upside?.missing_requirements)
      ? opp.estimated_upside.missing_requirements
      : reviewReasons
    ).map(formatReviewReason).filter(Boolean),
    economicsStatus:
      str(economics.economics_status) || str(opp?.economics_status) || null,
    economicsMissingInput:
      str(economics.economics_missing_input) || str(opp?.economics_missing_input) || null,
    candidateVerified: economics.candidate_verified === true,
    economicsKind: str(economics.economics_kind)
      || (economicsComplete
        ? (economics.excludes_wfs ? "fee_excluded_estimate" : "complete_net_profit")
        : profit != null
          ? "fee_excluded_estimate"
          : "incomplete"),
    offerVerification: str(opp?.offer_evidence?.offer_verification)
      || str(wm.availability_evidence),
    activeOfferConfirmed: opp?.offer_evidence?.active_offer_confirmed === true,

    // ── status / timestamps ──────────────────────────────────────────────────
    reviewStatus: str(opp?.review_status),
    saved: !!opp?.saved,
    createdAt: str(opp?.created_at),
    updatedAt: str(opp?.updated_at),
    checkedAt: str(wm.checked_at),
    collectedAt: str(src.collected_at),
    canAnalyze,

    // ── provenance (real, if present) ────────────────────────────────────────
    profileId: str(opp?.auto_sourcing_profile_id),
    runId: str(opp?.auto_sourcing_run_id),
    jobId: str(opp?.job_id),
    collectorVersion: str(opp?.collector_version),
    provider: str(wm.provider),
    imageSource: str(wm.image_source),

    // ── NOT persisted by the pipeline → null (UI renders "—") ────────────────
    salesPerMonth: null,
  };
}

/** Canonical empty lead for tests / skeletons (never rendered as a fixture). */
export function emptyDealFlowLead(over = {}) {
  return {
    id: "—",
    marketplace: "walmart",
    sourceMerchant: "Source",
    sourceDomain: null,
    sourceUrl: null,
    sourceImage: null,
    sourceImages: [],
    sourceTitle: null,
    sourcePrice: null,
    sourcePriceScope: null,
    sourcePriceLocationId: null,
    sourceCurrency: "USD",
    sourceAvailability: null,
    sourceBrand: null,
    sourceDescription: null,
    sourceFeatures: [],
    sourceSpecifications: null,
    sourceModel: null,
    sourceMpn: null,
    sourceSize: null,
    sourceColor: null,
    sourcePackCount: null,
    sourceCondition: null,
    sourceShipping: null,
    sourceListPrice: null,
    walmartItemId: null,
    walmartUrl: null,
    walmartImage: null,
    walmartImages: [],
    walmartTitle: null,
    walmartPrice: null,
    walmartCurrency: "USD",
    walmartAvailability: null,
    walmartSeller: null,
    walmartSellerCount: null,
    walmartBrand: null,
    walmartManufacturer: null,
    walmartDescription: null,
    walmartFeatures: [],
    walmartSpecifications: null,
    walmartModel: null,
    walmartMpn: null,
    walmartSize: null,
    walmartColor: null,
    walmartCondition: null,
    walmartPack: null,
    walmartPackCount: null,
    walmartListPrice: null,
    walmartFulfillment: null,
    walmartDeliveryText: null,
    walmartPickupText: null,
    walmartShippingText: null,
    walmartSoldAndShippedBy: null,
    walmartAdditionalOfferCount: null,
    walmartAdditionalOffersStartingPrice: null,
    upc: null,
    gtin: null,
    category: null,
    spread: null,
    spreadPercent: null,
    packDiffers: false,
    sourceUnitPrice: null,
    walmartUnitPrice: null,
    unitSpread: null,
    priceBasis: "item",
    sourcePackLabel: null,
    walmartPackLabel: null,
    matchStatus: "unknown",
    matchStatusLabel: null,
    matchConfidence: null,
    matchTier: null,
    matchReason: null,
    matchEvidence: [],
    matchRejectionReasons: [],
    matchMatchedEvidence: [],
    matchMissingEvidence: [],
    reviewStatus: null,
    saved: false,
    createdAt: null,
    updatedAt: null,
    checkedAt: null,
    collectedAt: null,
    canAnalyze: false,
    rating: null,
    reviews: null,
    sellers: null,
    salesPerMonth: null,
    fees: null,
    profit: null,
    roi: null,
    margin: null,
    economicsComplete: false,
    roiDenominator: "effective_acquisition_cost",
    excludesWfs: false,
    excludesStorage: false,
    sourceProductId: null,
    qualification: null,
    identityRejected: false,
    availabilityState: null,
    availabilityStale: false,
    availabilityObservedAt: null,
    opportunityStatus: null,
    opportunityStatusLabel: null,
    opportunityStatusHint: null,
    reviewReasons: [],
    reviewReasonLabels: [],
    missingRequirements: [],
    economicsKind: "incomplete",
    economicsStatus: null,
    economicsMissingInput: null,
    candidateVerified: false,
    offerVerification: null,
    activeOfferConfirmed: false,
    profileId: null,
    runId: null,
    jobId: null,
    collectorVersion: null,
    provider: null,
    imageSource: null,
    ...over,
  };
}
